import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim()) {
      setFormStatus('success');
      return;
    }
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;
    if (!name || !email || !message) {
      setFormError('Please fill in all required fields.');
      setFormStatus('error');
      return;
    }
    setFormStatus('success');
    setFormError('');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-6 pb-12">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-3xl mx-auto">
          <nav className="text-sm text-stone-500 mb-6">
            <a href="/" className="hover:text-emerald-700 transition-colors">{t('breadcrumb_home')}</a>
            <span className="mx-2">/</span>
            <span className="text-stone-700">Contact</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6">Contact Us</h1>

          <p className="text-sm md:text-base text-stone-600 leading-relaxed mb-8">
            Have questions about CSSBuy Spreadsheet resources or need help with product research? We are here to help. Please fill out the form below and we will get back to you as soon as possible. Note that we are an independent resource and cannot assist with orders or payments.
          </p>

          {formStatus === 'success' ? (
            <div className="p-5 md:p-6 rounded-xl bg-emerald-50 border border-emerald-100 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-3">
                <i className="ri-check-line text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">Message Sent</h3>
              <p className="text-sm text-emerald-700">Thank you for reaching out. We will respond as soon as possible.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-1.5">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm text-stone-800 outline-none"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-1.5">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm text-stone-800 outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-stone-700 mb-1.5">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm text-stone-800 outline-none"
                  placeholder="What is this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-1.5">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-sm text-stone-800 outline-none resize-none"
                  placeholder="Your message (max 500 characters)"
                />
                <p className="mt-1 text-xs text-stone-400">Maximum 500 characters</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute opacity-0 pointer-events-none"
                style={{ position: 'absolute', left: '-9999px' }}
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors text-sm whitespace-nowrap"
              >
                Send Message
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}